import { makeObservable, observable, computed, action } from 'mobx';
import { PTestQuestion, PTestCollection, getPTestQuestions } from './onboarding.service';
import { PTestAnswerType, UserRole } from '../user/user.type';

export default class PTestStore {
  questions: PTestQuestion[] = [];
  answers: PTestAnswerType[] = [];
  currentPage: number = 0;
  questionsPerPage: number = 3;
  loading: boolean = false;
  error: string | null = null;

  constructor() {
    makeObservable(this, {
      questions: observable,
      answers: observable,
      currentPage: observable,
      questionsPerPage: observable,
      loading: observable,
      error: observable,

      totalPages: computed,
      currentQuestions: computed,
      progress: computed,
      canGoNext: computed,
      canGoPrev: computed,
      isContinuable: computed,
      isComplete: computed,

      loadQuestions: action,
      updateAnswer: action,
      nextPage: action,
      prevPage: action,
      goToPage: action,
      setLoading: action,
      setError: action,
      setQuestions: action,
      calculateTotalScore: action,
      calculatePersonalityCode: action,
      calculateResilienceFlag: action,
    });
  }

  get totalPages(): number {
    return Math.ceil(this.questions.length / this.questionsPerPage);
  }

  get currentQuestions(): PTestQuestion[] {
    const startIndex = this.currentPage * this.questionsPerPage;
    const endIndex = startIndex + this.questionsPerPage;
    return this.questions.slice(startIndex, endIndex);
  }

  get progress(): number {
    if (this.questions.length === 0) return 0;
    return this.answers.length / this.questions.length;
  }

  get canGoNext(): boolean {
    return this.currentPage < this.totalPages - 1;
  }

  get canGoPrev(): boolean {
    return this.currentPage > 0;
  }

  get isContinuable(): boolean {
    return this.currentQuestions.every(question => {
      const questionId = question.id!;
      return this.getAnswer(questionId) !== undefined;
    });
  }

  get isComplete(): boolean {
    return this.answers.length === this.questions.length;
  }

  async loadQuestions(collection: PTestCollection.PERSONALITY_D | PTestCollection.PERSONALITY_E): Promise<void> {
    try {
      this.setLoading(true);
      this.setError(null);
      const questions = await getPTestQuestions(collection);
      this.setQuestions(questions);
    } catch (error) {
      this.setError('Failed to load questions. Please try again.');
    } finally {
      this.setLoading(false);
    }
  }

  updateAnswer(questionId: string, answer: number, category: string, isFlipped: boolean): void {
    // Remove existing answer for this question if it exists
    this.answers = this.answers.filter(a => a.questionId !== questionId);

    // Add new answer
    const newAnswer: PTestAnswerType = {
      questionId,
      answer,
      category,
      isFlipped,
    };

    this.answers.push(newAnswer);
  }

  nextPage(): void {
    if (this.canGoNext) {
      this.currentPage += 1;
    }
  }

  prevPage(): void {
    if (this.canGoPrev) {
      this.currentPage -= 1;
    }
  }

  goToPage(page: number): void {
    if (page >= 0 && page < this.totalPages) {
      this.currentPage = page;
    }
  }

  setLoading(loading: boolean): void {
    this.loading = loading;
  }

  setError(error: string | null): void {
    this.error = error;
  }

  setQuestions(questions: PTestQuestion[]): void {
    this.questions = questions;
  }

  getAnswer(questionId: string): PTestAnswerType | undefined {
    return this.answers.find(a => a.questionId === questionId);
  }

  calculateTotalScore(): number {
    return this.answers.reduce((total, answer) => {
      if (answer.isFlipped) {
        return total + (6 - answer.answer);
      } else {
        return total + answer.answer;
      }
    }, 0);
  }

  private calculateTraitScores() {
    const traitScores = {
      Extraversion: 0,
      Openness: 0,
      Agreeableness: 0,
      Conscientiousness: 0,
      Resilience: 0,
    };

    this.answers.forEach(({ answer, category, isFlipped }) => {
      const score = isFlipped ? 6 - answer : answer;
      if (traitScores[category as keyof typeof traitScores] !== undefined) {
        traitScores[category as keyof typeof traitScores] += score;
      }
    });

    return traitScores;
  }

  calculatePersonalityCode(userRole: UserRole | ''): string {
    const traitScores = this.calculateTraitScores();

    // Determine prefix based on role
    const updatedCodePrefix = userRole === UserRole.Founder ? 'F' : 'D';

    // Calculate personality code based on trait scores
    const code = [
      traitScores.Extraversion >= 18.5 ? 'E' : 'I',
      traitScores.Openness >= 18.5 ? 'O' : 'D',
      traitScores.Agreeableness >= 18.5 ? 'A' : 'H',
      traitScores.Conscientiousness >= 18.5 ? 'C' : 'D',
    ].join('');

    return `${updatedCodePrefix}${code}`;
  }

  calculateResilienceFlag(): string {
    const traitScores = this.calculateTraitScores();
    return traitScores.Resilience >= 18.5 ? 'N' : 'R';
  }
}
